/**
 * UI preferences menu — the gear-icon popover (Flow P + Flow U).
 *
 * # Part 2 (architect 2026-06-08): gear-menu reorganization
 *
 * The named-preset Layout submenu is **gone** (presets were removed — layouts
 * are captured live on save into a Profile). The menu is now, top to bottom:
 *
 * 1. **Theme** — Dark / Light / System; instant-apply (unchanged).
 * 2. **Settings** — opens the live Settings drawer (``open()`` from the
 *    Settings-panel store). A top-level action item.
 * 3. **Panels** (visible only when the multi-pane workspace is mounted) — the
 *    show-hidden-panels inventory (formerly "Add panel"), plus a **"Reset
 *    layout to profile"** action that re-applies the **active** Profile's
 *    layout to the live dockview (``applyLayout``).
 * 4. **Profiles** — grouped Shared / User. Per row: ▶ Apply, Export, **Update
 *    current Profile** (the pencil — diff-aware over the FULL live document
 *    incl. layout + map camera; "Nothing to update" only when already
 *    identical), Delete, and **Set as default** (the login-default pointer).
 *    Below: **New Profile…** (a name prompt that saves the live config via the
 *    shared :func:`useSaveProfile` helper) + **Import Profile…**.
 * 5. **Reset to profile** — resets ALL config (working state + layout +
 *    viewport) to the active Profile (re-fetch + ``applyWorkingState`` +
 *    ``applyLayout``); falls back to the built-in default when no Profile is
 *    active (generalises the post-inspection 4a behaviour).
 * 6. **Inline error banner** — on PUT / export / save failure (REQ-UI-037).
 *
 * The expandable sections (Theme / Panels / Profiles) are **mutually
 * exclusive** — opening one collapses the others.
 *
 * Cross-references:
 *   - REQ-UI-040 / 041 / 042 / 043 (theme + menu surface + reset).
 *   - REQ-UI-047 (role-filtered panel inventory).
 *   - REQ-UI-054 / 055 (reset layout to the active Profile / default).
 *   - REQ-UI-059 (Ctrl+Shift+P opens the Panels section).
 *   - REQ-UI-074 / 075 (Apply + active-Profile + set-default + "(modified)").
 *   - REQ-UI-076 (Settings drawer; New saves the live config).
 *   - REQ-UI-079 / 080 (admin-gated Edit / Delete affordances).
 *   - REQ-UI-081 / 082 (export / import a Profile).
 *   - REQ-UI-094 (labelled, keyboard-operable affordances).
 *   - REQ-AUTH-065 (Profiles list shape — seeded from bootstrap).
 *   - ADR-022 §Feature 2 + 3, ADR-024 §6, ADR-033 §3.2 / §6.1.
 *   - chrome-spec.md 2026-05-26 amendment §"UI preferences menu".
 */
import * as Popover from "@radix-ui/react-popover";
import {
  ArrowCounterClockwise,
  Bell,
  Export,
  Gear,
  Moon,
  Monitor,
  PencilSimple,
  Play,
  Plus,
  PushPin,
  PushPinSlash,
  SlidersHorizontal,
  Sun,
  Trash,
  UploadSimple,
  UserCircle,
} from "@phosphor-icons/react";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { useSession } from "@/auth/session-context";
import type {
  ActiveProfileRef,
  DefaultProfileRef,
  ProfileDocument,
  ProfileSummary,
  ThemeMode,
} from "@/auth/session-types";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { deleteProfile } from "@/profiles/profiles-client";
import { parseProfileImport } from "@/profiles/profiles-import";
import { sha256CanonicalJson } from "@/profiles/profile-hash";
import { useApplyProfile } from "@/profiles/use-apply-profile";
import { useSaveProfile } from "@/profiles/use-save-profile";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { PANEL_TITLES, type PanelId, getVisiblePanels } from "@/multi-pane/registry-types";
import { buildDefaultWorkingState } from "@/preferences/default-working-state";
import { usePreferences } from "@/preferences/preferences-context";
import {
  DEFAULT_ALERT_NOTIFICATION_THRESHOLD,
  NOTIFICATION_SCHEMA_VERSION,
  type AlertNotificationThreshold,
} from "@/alerts/alert-notification";
import { useProfileEditorActions } from "@/profiles/profile-editor-store";
import { useProfiles } from "@/profiles/profiles-context";
import { useResolvedTheme } from "@/theme/resolve-theme";
import { type Theme } from "@/theme/tokens";

/**
 * Display ordering for the three theme options. Dark first per
 * the 2026-05-26 amendment (production is dark-first).
 */
const THEME_OPTIONS: readonly Theme[] = ["dark", "light", "system"] as const;

const THEME_LABEL: Record<Theme, string> = {
  dark: "Dark",
  light: "Light",
  system: "System",
};

/**
 * Phosphor icons accept ``size: string | number`` plus a
 * ``weight`` prop for the regular / fill / bold / duotone
 * variants. We type our icon map permissively so consumer call
 * sites can pass either.
 */
type PhosphorIconLike = ComponentType<{
  size?: string | number;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
}>;

const THEME_ICON: Record<Theme, PhosphorIconLike> = {
  dark: Moon,
  light: Sun,
  system: Monitor,
};

/**
 * The mutually-exclusive expandable sections in the popover.
 * ``null`` means every expandable section is collapsed. (Settings
 * is a direct action, not an expandable section.)
 */
type ExpandedSection = "theme" | "notifications" | "panels" | "profiles" | null;

/**
 * The inline name+scope prompt's state. ``null`` = collapsed. The two modes
 * share the SAME prompt component (ADR-033 §11.2):
 *   - ``"new"`` — name the current live config (saved via the live-capture
 *     helper);
 *   - ``"import"`` — name a Profile created from the ``document`` of a
 *     validated import wrapper, ``name`` prefilled from the export.
 */
type ProfilePromptState =
  { mode: "new" } | { mode: "import"; name: string; document: ProfileDocument } | null;

/**
 * The preferences menu — cog button + Radix Popover shell.
 *
 * Owns the popover open state and the mutually-exclusive
 * section expansion. The cog trigger is rendered here (not in
 * :class:`TopBar`) so the trigger + popover lifecycle stays
 * encapsulated.
 */
export function PreferencesMenu(): React.ReactElement {
  const {
    workingState,
    activeProfile,
    defaultProfile,
    setWorkingState,
    applyWorkingState,
    resetPreferences,
    setDefaultProfile,
  } = usePreferences();
  const { hasAnyRole } = useSession();
  const { profiles, getDocument, exportProfile, refresh } = useProfiles();
  const { open: openSettings } = useProfileEditorActions();
  // Shared save machinery — the SAME helper the Settings panel's "Save as new
  // Profile" uses (so New Profile… here captures the live layout + viewport
  // identically — ADR-033 §5) plus the from-document variant that backs Import
  // (create from the imported document, not the live config — ADR-033 §11.2).
  const { saveProfileAsNew, saveProfileFromDocument, updateProfileFromLive } = useSaveProfile();
  // Shared apply-Profile machinery — the SAME path the top-bar Profile switcher
  // uses (REQ-UI-073 / 075), so Apply here and quick-switch there are identical.
  const { applyProfile } = useApplyProfile();
  const multiPaneApi = useMultiPaneApi();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<ExpandedSection>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The inline name+scope prompt (collapsed = ``null``). Two modes share the
  // SAME prompt UI (ADR-033 §11.2 — import reuses the New-Profile prompt):
  //   - ``{ mode: "new" }`` — name the LIVE config, save via saveProfileAsNew;
  //   - ``{ mode: "import", name, document }`` — name a Profile created from the
  //     IMPORTED document (prefilled name), save via saveProfileFromDocument.
  const [profilePrompt, setProfilePrompt] = useState<ProfilePromptState>(null);
  // Hidden file input backing "Import Profile…" (REQ-UI-082). The visible
  // menu item is keyboard-reachable; clicking it forwards to this input's
  // native picker. Held as a ref so we can reset ``value`` after each import
  // (re-importing the same file must re-fire ``change``).
  const importInputRef = useRef<HTMLInputElement>(null);

  // Edit/Delete affordances on a Shared (``scope: "global"``) Profile are
  // admin-only (ADR-033 D2 / REQ-UI-079). The BFF is the primary control
  // (REQ-AUTH-074); this gating is UX-only.
  const isAdmin = hasAnyRole(["admin"]);

  const currentTheme = coerceTheme(workingState.theme);
  // Absent section (a Profile saved before it existed) ⇒ the shipped default,
  // which is `off` since 2026-08-31. Note the direction reversed with it: an
  // older Profile now surfaces FEWER alerts, never more. Documents that stored
  // an explicit `all` are moved by the BFF's v1→v2 notifications migration, so
  // this fallback and that migration have to agree on the default.
  const currentAlertThreshold: AlertNotificationThreshold =
    workingState.notifications?.sapient_alert_threshold ?? DEFAULT_ALERT_NOTIFICATION_THRESHOLD;
  const systemResolved = useResolvedTheme("system");

  // Ctrl+Shift+P (Cmd+Shift+P on macOS) opens the Panels submenu
  // per REQ-UI-059. Gated on workspace presence: the shortcut is
  // dead on routes without dockview mounted (e.g., /403). The
  // keydown listener detaches when the workspace unmounts so other
  // shortcuts can rebind the chord.
  useEffect(() => {
    if (multiPaneApi === null) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      const modifier =
        typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC")
          ? event.metaKey
          : event.ctrlKey;
      // ``event.key`` is "P" with shift, "p" without — handle both
      // since some layouts upper-case unconditionally under shift.
      const isP = event.key === "p" || event.key === "P";
      if (modifier && event.shiftKey && isP) {
        event.preventDefault();
        setOpen(true);
        setExpanded("panels");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [multiPaneApi]);

  // Reset transient state when the menu closes so reopening
  // doesn't surface stale errors or stale submenu expansion.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) {
      setExpanded(null);
      setError(null);
      setProfilePrompt(null);
    }
  };

  const toggleSection = (section: "theme" | "notifications" | "panels" | "profiles"): void => {
    setExpanded((prev) => (prev === section ? null : section));
  };

  // Settings opens the live Settings drawer; close the popover so the operator
  // sees the drawer + the live canvas behind it (REQ-UI-076).
  const handleOpenSettings = (): void => {
    setError(null);
    setExpanded(null);
    setOpen(false);
    openSettings();
  };

  const handleExportProfile = async (profileId: string): Promise<void> => {
    setError(null);
    setBusy(true);
    const result = await exportProfile(profileId);
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
    }
    // On success the browser download has fired; keep the menu open
    // so the operator can export another profile without reopening.
  };

  const handleAlertThresholdSelect = async (next: AlertNotificationThreshold): Promise<void> => {
    setError(null);
    setBusy(true);
    // ``notifications`` is a working-state slice like ``theme`` (ADR-033 §3.1);
    // patch just that section — the provider round-trips the rest. The stamp
    // must be the CURRENT version: the BFF migrates an older section on save,
    // and a v1 ``all`` is persisted as ``off`` (see NOTIFICATION_SCHEMA_VERSION;
    // REQ-UI-130, Flow F F.12).
    const result = await setWorkingState({
      notifications: {
        notification_schema_version: NOTIFICATION_SCHEMA_VERSION,
        sapient_alert_threshold: next,
      },
    });
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
    }
  };

  const handleThemeSelect = async (next: Theme): Promise<void> => {
    setError(null);
    setBusy(true);
    // Theme is a working-state slice (ADR-033 §3.1); patch just
    // ``theme`` — the provider round-trips the rest of the document.
    const result = await setWorkingState({ theme: next });
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
    }
    // Menu stays open — the user might want to try another option
    // before closing. Appaloosa pattern.
  };

  // Apply a Profile (REQ-UI-073 / 075; ADR-033 §4.2). Fetch the
  // Profile document, copy it into the working state by value, set
  // ``active_profile`` (with a content hash so "(modified)" can be
  // derived later), and PUT — all via the preferences working-state
  // writer. Closes the popover on success so the operator sees the
  // applied configuration on the canvas.
  const handleApplyProfile = async (summary: ProfileSummary): Promise<void> => {
    // Apply via the SHARED :func:`useApplyProfile` — the SAME path the top-bar
    // Profile switcher uses (REQ-UI-073 / 075; ADR-033 §4.2) — so both surfaces
    // copy-by-value + restore the captured layout identically (no duplicated
    // apply logic; CLAUDE.md §4.4). The hook owns the apply mechanics; the menu
    // owns its busy / error / close UX.
    setError(null);
    setBusy(true);
    const result = await applyProfile(summary.profile_id);
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
      return;
    }
    // Applied — close the popover so the operator sees the live canvas.
    setExpanded(null);
    setOpen(false);
  };

  // New Profile… opens the inline name prompt (NOT the Settings panel) — the
  // operator names the current live config; the shared save-as-new helper
  // captures the live layout + viewport into the new Profile (REQ-UI-076).
  const handleNewProfile = (): void => {
    setError(null);
    setExpanded("profiles");
    setProfilePrompt({ mode: "new" });
  };

  // Save the current live config as a NEW Profile via the shared helper
  // (createProfile + apply-by-value). The prompt supplies a trimmed name + a
  // scope (Shared admin-gated in the prompt; the BFF is the primary control).
  // On success the new Profile is active; close the popover so the operator
  // sees the applied config on the canvas.
  const handleSaveNewProfile = async (name: string, scope: "global" | "user"): Promise<void> => {
    setError(null);
    setBusy(true);
    const result = await saveProfileAsNew(name, scope);
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
      return;
    }
    setProfilePrompt(null);
    setExpanded(null);
    setOpen(false);
  };

  // Per-profile "Update current Profile" (the pencil — relabelled from "Edit"
  // 2026-06-08; it updates THIS profile to the current live state, REQ-UI-076).
  // Routes through the SHARED :func:`useSaveProfile().updateProfileFromLive` so
  // it captures + diffs the **full** live document — working_state + live
  // layout + live map camera — identically to the Settings-panel footer button.
  // A pan/zoom or panel rearrange now counts as a difference (previously this
  // diffed working_state alone → a false "Nothing to update"). The destructive-
  // overwrite ``window.confirm`` is passed as the gate, invoked only when there
  // IS a difference; the helper re-baselines the active pointer when the edited
  // profile is the active one so "(modified)" clears.
  const handleEditProfile = async (summary: ProfileSummary): Promise<void> => {
    setError(null);
    setBusy(true);
    const result = await updateProfileFromLive(summary, {
      confirm: () =>
        window.confirm(
          `Update the profile "${summary.name}" to your current settings? This replaces its saved configuration.`,
        ),
    });
    setBusy(false);
    if (result.status === "unchanged") {
      setError(`Nothing to update — "${summary.name}" already matches your settings.`);
      return;
    }
    if (result.status === "error") {
      setError(result.message);
    }
    // "ok" / "cancelled": nothing to surface (the menu stays open).
  };

  // Set (or clear) the user's login-default Profile pointer (ADR-033 §3.2).
  // Toggling the current default off clears it (login then falls back to the
  // built-in Shared default); selecting another sets it. The working state +
  // active-Profile pointer are unchanged — this only moves the login default.
  const handleSetDefault = async (
    summary: ProfileSummary,
    isCurrentDefault: boolean,
  ): Promise<void> => {
    setError(null);
    setBusy(true);
    const ref: DefaultProfileRef | null = isCurrentDefault
      ? null
      : { scope: summary.scope, profile_id: summary.profile_id, name: summary.name };
    const result = await setDefaultProfile(ref);
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
    }
    // Menu stays open so the operator sees the updated default marker.
  };

  // Import Profile… (REQ-UI-082 / ADR-033 §11.2 / Flow U). The menu item just
  // opens the native file picker; the actual parse + apply happens in
  // :func:`handleImportFile` when a file is chosen. Kept as two steps so the
  // click handler stays synchronous (it only triggers the input).
  const handleImportClick = (): void => {
    setError(null);
    importInputRef.current?.click();
  };

  // A file was chosen for import (ADR-033 §11.2 / Flow U). Read its text +
  // validate the export wrapper, then either (ok) open the SAME name+scope
  // prompt the New-Profile flow uses — prefilled with the export's name, scope
  // defaulting to User (Shared admin-gated in the prompt) — so the operator
  // confirms and a NEW Profile is created from the imported document; or (!ok)
  // surface the parse error inline. Nothing is loaded live and nothing is
  // created until the operator confirms the prompt.
  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    // Always clear the input value so re-selecting the same file re-fires
    // ``change`` (browsers suppress an identical-value change otherwise).
    event.target.value = "";
    if (file === undefined) {
      return;
    }
    setError(null);
    let text: string;
    try {
      text = await file.text();
    } catch {
      // FileReader failure (e.g. the file vanished / permission). Generic
      // copy per REQ-UI-037; nothing is loaded.
      setError("Couldn't read that file — please try again.");
      return;
    }
    const result = parseProfileImport(text);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    // Open the name+scope prompt in IMPORT mode: prefill the name from the
    // export and carry the imported document. Creation happens on confirm via
    // :func:`handleConfirmImport` → ``saveProfileFromDocument``.
    setExpanded("profiles");
    setProfilePrompt({ mode: "import", name: result.name, document: result.document });
  };

  // Confirm the import prompt — create a NEW Profile from the IMPORTED document
  // (not the live config) via the shared from-document helper, which applies it
  // by value on success (consistent with New-Profile). The prompt supplies a
  // trimmed name + scope (Shared admin-gated in the prompt; the BFF is the
  // primary control). On 409 the helper returns the "already exists" message —
  // surfaced inline for rename, the prompt staying open. On success close so
  // the operator sees the applied config.
  const handleConfirmImport = async (
    name: string,
    scope: "global" | "user",
    document: ProfileDocument,
  ): Promise<void> => {
    setError(null);
    setBusy(true);
    const result = await saveProfileFromDocument(name, scope, document);
    setBusy(false);
    if (result.status === "error") {
      setError(result.message);
      return;
    }
    setProfilePrompt(null);
    setExpanded(null);
    setOpen(false);
  };

  // Delete from the gear menu (REQ-UI-080): confirm + deleteProfile,
  // then refresh the list. Built-in / non-admin-Shared rows never render
  // a Delete affordance (the ProfileRow gating), so this only runs for
  // deletable Profiles. The menu stays open so the operator sees the
  // updated list (or an inline error).
  const handleDeleteProfile = async (summary: ProfileSummary): Promise<void> => {
    const proceed = window.confirm(`Delete the profile "${summary.name}"? This cannot be undone.`);
    if (!proceed) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await deleteProfile(summary.profile_id);
      await refresh();
      setBusy(false);
    } catch (error) {
      setBusy(false);
      // 409 = built-in (the UI prevents reaching it); everything else is
      // a generic retry per REQ-UI-037.
      setError(
        error instanceof ApiError && error.status === 409
          ? "Built-in profiles can't be deleted."
          : "Couldn't delete this profile — please try again.",
      );
    }
  };

  const handleTogglePanel = (id: PanelId): void => {
    if (multiPaneApi === null) {
      return;
    }
    setError(null);
    multiPaneApi.togglePanel(id);
    // Menu stays open — operator may toggle several panels in
    // a row without reopening the popover.
  };

  // "Reset layout to profile" (part 2) — re-applies the **active** Profile's
  // layout to the live dockview via :func:`MultiPaneApi.applyLayout`. Fetches
  // the active Profile's document for its layout slice; falls back to
  // :func:`MultiPaneApi.resetLayout` (the built-in default) when no Profile is
  // active or it carries no layout. No-op without a mounted workspace.
  // ``applyLayout`` itself role-gates + corrupt-falls-back, so a stale blob is
  // safe.
  const handleResetLayoutToProfile = async (): Promise<void> => {
    if (multiPaneApi === null) {
      return;
    }
    if (activeProfile === null) {
      multiPaneApi.resetLayout();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const detail = await getDocument(activeProfile.profile_id);
      setBusy(false);
      if (detail.document.layout !== undefined) {
        multiPaneApi.applyLayout(detail.document.layout);
      } else {
        multiPaneApi.resetLayout();
      }
    } catch {
      // The active Profile vanished / access lost. Fall back to the built-in
      // default layout so the action still does something safe.
      setBusy(false);
      multiPaneApi.resetLayout();
    }
  };

  // Re-apply a reset document's layout to the LIVE dockview (item 4a). The
  // workspace applies layout imperatively (``fromJSON``), not reactively off
  // the working state — so reverting ``working_state.layout`` alone leaves
  // the operator's unsaved panel arrangement on screen. Drive the
  // ``applyLayout`` primitive with the reset document's layout; fall back to
  // ``resetLayout`` (the built-in default) when the reset document carries no
  // layout (e.g. the built-in default working state). No-op when no workspace
  // is mounted (e.g. /403). ``applyLayout`` itself role-gates + corrupt-falls-
  // back, so a stale/garbage blob is safe.
  const reapplyResetLayout = (doc: ProfileDocument): void => {
    if (multiPaneApi === null) {
      return;
    }
    if (doc.layout !== undefined) {
      multiPaneApi.applyLayout(doc.layout);
    } else {
      multiPaneApi.resetLayout();
    }
  };

  // "Reset to profile" (part 2; relabelled from "Revert changes" / "Reset to
  // defaults"). Resets ALL config — working state + layout + viewport — to the
  // active Profile, discarding unsaved tweaks AND the unsaved dockview
  // arrangement:
  //   - a Profile is active ⇒ revert to that Profile's saved document (re-
  //     fetch + ``applyWorkingState`` — which also re-flies the map to the
  //     Profile's ``map.home`` via the Map's home-change effect), then re-apply
  //     its layout to the live dockview;
  //   - no Profile active ⇒ reset to the built-in default working state
  //     (REQ-UI-043) + snap the layout to the built-in default.
  // Either way the canvas + the workspace end up matching the reset state, not
  // the operator's discarded edits.
  const handleReset = async (): Promise<void> => {
    setError(null);
    setBusy(true);

    if (activeProfile !== null) {
      // Reset to the active Profile (discard unsaved tweaks). Mirrors
      // ``handleApplyProfile`` — re-fetch so we revert to the SAVED document,
      // not a possibly-modified working-state copy.
      try {
        const detail = await getDocument(activeProfile.profile_id);
        const doc = detail.document;
        const ref: ActiveProfileRef = {
          scope: detail.scope,
          profile_id: detail.profile_id,
          name: detail.name,
          applied_at: new Date().toISOString(),
          applied_document_sha256: await sha256CanonicalJson(doc),
        };
        const result = await applyWorkingState(doc, ref);
        setBusy(false);
        if (result.status === "error") {
          setError(result.message);
          return;
        }
        reapplyResetLayout(doc);
        setExpanded(null);
        setOpen(false);
        return;
      } catch {
        // The active Profile vanished / access lost between apply and reset.
        // Fall through to the built-in default reset below so "Reset to
        // profile" still does something safe.
        setBusy(true);
      }
    }

    // No active Profile (or its document is gone) ⇒ reset to the built-in
    // default working state + default layout.
    const result = await resetPreferences();
    setBusy(false);
    if (result.status === "ok") {
      reapplyResetLayout(buildDefaultWorkingState());
      // Successful reset — collapse + close so the user sees the
      // applied default on the underlying canvas + workspace.
      setExpanded(null);
      setOpen(false);
    } else {
      setError(result.message);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="UI preferences"
          title="UI preferences"
          className="rounded p-1.5 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
          data-testid="preferences-gear"
        >
          <Gear size={18} />
        </button>
      </Popover.Trigger>
      {/* Hidden file input backing "Import Profile…" (REQ-UI-082). Visually
       * hidden but in the DOM whenever the menu is mounted; the visible menu
       * item (keyboard-reachable) triggers its native picker. ``aria-hidden``
       * keeps the redundant control out of the a11y tree — the labelled menu
       * item is the announced affordance. */}
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="preferences-menu-profiles-import-input"
        onChange={(event) => {
          void handleImportFile(event);
        }}
      />
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          className={cn(
            "z-50 min-w-[240px] overflow-hidden rounded-lg",
            "border border-border bg-bg-overlay shadow-lg",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0",
            "data-[state=open]:zoom-in-95",
          )}
          data-testid="preferences-menu"
          aria-label="UI preferences"
        >
          {/* Theme section — collapsible, mutually exclusive. */}
          <ThemeSection
            expanded={expanded === "theme"}
            onToggleExpanded={() => toggleSection("theme")}
            currentTheme={currentTheme}
            systemResolved={systemResolved}
            onSelect={(t) => {
              void handleThemeSelect(t);
            }}
            busy={busy}
          />

          {/* SAPIENT alert notifications (ADR-029 §6.1 / REQ-UI-062). */}
          <NotificationsSection
            expanded={expanded === "notifications"}
            onToggleExpanded={() => toggleSection("notifications")}
            current={currentAlertThreshold}
            onSelect={(t) => {
              void handleAlertThresholdSelect(t);
            }}
            busy={busy}
          />

          {/* Settings — a top-level action that opens the live Settings drawer
           * (REQ-UI-076). Not an expandable section. */}
          <div className="border-t border-border" />
          <button
            type="button"
            disabled={busy}
            onClick={handleOpenSettings}
            data-testid="preferences-menu-settings"
            className={cn(
              "flex w-full items-center gap-2 px-4 py-2.5",
              "text-body-sm text-text-primary hover:bg-bg-elevated",
              "focus:outline-none focus:bg-bg-elevated",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <SlidersHorizontal size={14} />
            <span>Settings</span>
          </button>

          {/* Panels section — visible only when the multi-pane workspace is
           * mounted in the route subtree. Lists the role-filtered panel
           * inventory + a "Reset layout to profile" action. Ctrl+Shift+P opens
           * the popover + expands this section directly (REQ-UI-059). */}
          {multiPaneApi !== null && (
            <>
              <div className="border-t border-border" />
              <PanelsSection
                expanded={expanded === "panels"}
                onToggleExpanded={() => toggleSection("panels")}
                visiblePanels={getVisiblePanels(hasAnyRole)}
                openPanelIds={multiPaneApi.openPanelIds}
                onTogglePanel={handleTogglePanel}
                onResetLayout={() => {
                  void handleResetLayoutToProfile();
                }}
                busy={busy}
              />
            </>
          )}

          {/* Profiles section — ADR-033 §6.1 / Flow U. Always visible
           * (Profiles are not layout-gated, unlike Panels). */}
          <div className="border-t border-border" />
          <ProfilesSection
            expanded={expanded === "profiles"}
            onToggleExpanded={() => toggleSection("profiles")}
            profiles={profiles}
            activeProfileId={activeProfile?.profile_id ?? null}
            defaultProfileId={defaultProfile?.profile_id ?? null}
            isAdmin={isAdmin}
            busy={busy}
            prompt={profilePrompt}
            onApply={(summary) => {
              void handleApplyProfile(summary);
            }}
            onExport={(id) => {
              void handleExportProfile(id);
            }}
            onEdit={(summary) => {
              void handleEditProfile(summary);
            }}
            onDelete={(summary) => {
              void handleDeleteProfile(summary);
            }}
            onSetDefault={(summary, isCurrent) => {
              void handleSetDefault(summary, isCurrent);
            }}
            onNew={handleNewProfile}
            onConfirmPrompt={(name, scope) => {
              // Route by the open prompt's mode: New saves the live config;
              // Import creates from the carried imported document.
              if (profilePrompt?.mode === "import") {
                void handleConfirmImport(name, scope, profilePrompt.document);
              } else {
                void handleSaveNewProfile(name, scope);
              }
            }}
            onCancelPrompt={() => setProfilePrompt(null)}
            onImport={handleImportClick}
          />

          <div className="border-t border-border" />

          {/* Reset to profile — destructive secondary action (REQ-UI-043).
           * Resets ALL config (working state + layout + viewport) to the
           * active Profile (the common case — a Profile is auto-applied on
           * first run per D9), else the built-in default. The label is stable
           * ("Reset to profile") — the Panels section's "Reset layout to
           * profile" above is the layout-only counterpart. */}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void handleReset();
            }}
            data-testid="preferences-menu-reset"
            className={cn(
              "flex w-full items-center gap-2 px-4 py-2.5",
              "text-body-sm text-destructive hover:bg-bg-elevated",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "focus:outline-none focus:bg-bg-elevated",
            )}
          >
            <ArrowCounterClockwise size={14} />
            <span>Reset to profile</span>
          </button>

          {error !== null && (
            <div
              role="alert"
              data-testid="preferences-menu-error"
              className={cn(
                "border-t border-border bg-bg-elevated px-4 py-2",
                "text-meta text-destructive",
              )}
            >
              {error}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Collapsible Theme section. Outer row toggles expansion; the
 * expanded body lists the three theme options inline with their
 * Phosphor icons + an "Active" pill on the currently-applied
 * choice.
 */
function ThemeSection({
  expanded,
  onToggleExpanded,
  currentTheme,
  systemResolved,
  onSelect,
  busy,
}: {
  expanded: boolean;
  onToggleExpanded: () => void;
  currentTheme: Theme;
  systemResolved: "light" | "dark";
  onSelect: (next: Theme) => void;
  busy: boolean;
}): React.ReactElement {
  const CurrentIcon = THEME_ICON[currentTheme];
  return (
    <div>
      <button
        type="button"
        onClick={onToggleExpanded}
        data-testid="preferences-menu-theme-toggle"
        aria-expanded={expanded}
        aria-controls="preferences-menu-theme-options"
        className={cn(
          "flex w-full items-center justify-between px-4 py-2.5",
          "text-body-sm text-text-primary hover:bg-bg-elevated",
          "focus:outline-none focus:bg-bg-elevated",
        )}
      >
        <span className="flex items-center gap-2">
          <CurrentIcon size={14} />
          Theme
        </span>
        <span
          className="text-text-muted text-meta capitalize"
          data-testid="preferences-menu-theme-current"
        >
          {currentTheme}
        </span>
      </button>
      {expanded && (
        <div
          id="preferences-menu-theme-options"
          role="group"
          aria-label="Theme options"
          className="border-t border-border bg-bg-base px-1 py-1"
          data-testid="preferences-menu-theme-options"
        >
          {THEME_OPTIONS.map((opt) => {
            const Icon = THEME_ICON[opt];
            const isActive = currentTheme === opt;
            const caption = opt === "system" ? `matches OS: ${systemResolved}` : undefined;
            return (
              <button
                key={opt}
                type="button"
                disabled={busy}
                onClick={() => onSelect(opt)}
                data-testid={`preferences-menu-theme-${opt}`}
                aria-current={isActive}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-3 py-2 text-left",
                  "text-body-sm focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  isActive
                    ? "bg-brand/10 text-brand"
                    : "text-text-primary hover:bg-bg-elevated focus:bg-bg-elevated",
                )}
              >
                <Icon size={14} weight={isActive ? "fill" : "regular"} />
                <span>{THEME_LABEL[opt]}</span>
                {caption !== undefined && (
                  <span className="text-text-muted text-meta">{caption}</span>
                )}
                {isActive && (
                  <span
                    className="ml-auto text-brand text-meta"
                    data-testid={`preferences-menu-theme-${opt}-active`}
                  >
                    Active
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Operator-facing labels + captions for the ADR-029 §6.1 threshold. */
const ALERT_THRESHOLD_OPTIONS: readonly AlertNotificationThreshold[] = ["all", "high_plus", "off"];

const ALERT_THRESHOLD_LABEL: Readonly<Record<AlertNotificationThreshold, string>> = {
  all: "All alerts",
  high_plus: "High and above",
  off: "Off",
};

/**
 * What each option does, shown beside its label. The shipped default's row
 * reads "default" instead (:func:`alertThresholdCaption`).
 */
const ALERT_THRESHOLD_CAPTION: Readonly<Record<AlertNotificationThreshold, string>> = {
  all: "every severity",
  high_plus: "medium and low stay in the panel",
  off: "no alert notifications",
};

/**
 * The caption for one option: "default" on whichever row is
 * ``DEFAULT_ALERT_NOTIFICATION_THRESHOLD`` (storyboard Flow F Frame 1 —
 * ``[default: Off]``), its description otherwise.
 *
 * Derived rather than written into the table because the hand-placed version
 * drifted: "default" stayed on "All alerts" for three weeks after the default
 * moved to ``off`` (2026-08-31 → 2026-09-21), telling operators the opposite of
 * the storyboard about a setting that now also silences CRITICAL.
 */
function alertThresholdCaption(threshold: AlertNotificationThreshold): string {
  return threshold === DEFAULT_ALERT_NOTIFICATION_THRESHOLD
    ? "default"
    : ALERT_THRESHOLD_CAPTION[threshold];
}

/**
 * Collapsible SAPIENT-alert notification section (ADR-029 §6.1, REQ-UI-062).
 *
 * **``Off`` is total, including CRITICAL** (architect direction 2026-08-31).
 * Until then the threshold governed toasts only and CRITICAL raised its sticky
 * banner under every setting — ADR-029 §6.1 ("sticky banners for CRITICAL
 * alerts are always shown") and Flow F ("not preference-controlled (always on,
 * can't disable)"). That bypass is removed.
 *
 * The standing note beneath the options is **retained and inverted** rather
 * than deleted. Its purpose was never to advertise a limitation — it was to
 * stop an operator mis-reading the scope of their own choice, and that purpose
 * now cuts the other way: "Off" really does mean silence, and an operator
 * needs to know that before an incident rather than during one. REQ-UI-130
 * requires the surface to state the behaviour; CLAUDE.md §8.1 (operator
 * visibility) is why it is stated at the point of choice.
 *
 * Shipped default is ``off`` (architect direction 2026-08-31; an interim
 * posture — see ``DEFAULT_ALERT_NOTIFICATION_THRESHOLD``). It was ``all`` from
 * 2026-07-26 (feature-spec §0.1 D1) and ADR-029 §6.1's as-written
 * ``high_plus`` before that.
 */
function NotificationsSection({
  expanded,
  onToggleExpanded,
  current,
  onSelect,
  busy,
}: {
  expanded: boolean;
  onToggleExpanded: () => void;
  current: AlertNotificationThreshold;
  onSelect: (next: AlertNotificationThreshold) => void;
  busy: boolean;
}): React.ReactElement {
  return (
    <div>
      <button
        type="button"
        onClick={onToggleExpanded}
        data-testid="preferences-menu-notifications-toggle"
        aria-expanded={expanded}
        aria-controls="preferences-menu-notifications-options"
        className={cn(
          "flex w-full items-center justify-between px-4 py-2.5",
          "text-body-sm text-text-primary hover:bg-bg-elevated",
          "focus:outline-none focus:bg-bg-elevated",
        )}
      >
        <span className="flex items-center gap-2">
          <Bell size={14} />
          Alert notifications
        </span>
        <span
          className="text-text-muted text-meta"
          data-testid="preferences-menu-notifications-current"
        >
          {ALERT_THRESHOLD_LABEL[current]}
        </span>
      </button>
      {expanded && (
        <div
          id="preferences-menu-notifications-options"
          role="group"
          aria-label="Alert notification options"
          className="border-t border-border bg-bg-base px-1 py-1"
          data-testid="preferences-menu-notifications-options"
        >
          {ALERT_THRESHOLD_OPTIONS.map((opt) => {
            const isActive = current === opt;
            return (
              <button
                key={opt}
                type="button"
                disabled={busy}
                onClick={() => onSelect(opt)}
                data-testid={`preferences-menu-notifications-${opt}`}
                aria-current={isActive}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-3 py-2 text-left",
                  "text-body-sm focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  isActive
                    ? "bg-brand/10 text-brand"
                    : "text-text-primary hover:bg-bg-elevated focus:bg-bg-elevated",
                )}
              >
                <span>{ALERT_THRESHOLD_LABEL[opt]}</span>
                <span
                  className="text-text-muted text-meta"
                  data-testid={`preferences-menu-notifications-${opt}-caption`}
                >
                  {alertThresholdCaption(opt)}
                </span>
                {isActive && (
                  <span
                    className="ml-auto text-brand text-meta"
                    data-testid={`preferences-menu-notifications-${opt}-active`}
                  >
                    Active
                  </span>
                )}
              </button>
            );
          })}
          {/* Retained and inverted 2026-08-31: same purpose (do not let an
           * operator mis-read the scope of their choice), opposite content now
           * that CRITICAL is gated too. */}
          <p
            className="px-3 pb-1 pt-2 text-text-muted text-meta"
            data-testid="preferences-menu-notifications-critical-note"
          >
            Off silences every alert notification, including critical alerts.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible Panels section (formerly "Add panel") — chrome-spec.md
 * 2026-05-26 amendment §"UI preferences menu (production)" + REQ-UI-047 / 059.
 * Lists every dockable primary panel the user's role permits + an open/closed
 * indicator dot per row; clicking a row routes through
 * :func:`MultiPaneApi.togglePanel` (focuses already-open panels; adds closed
 * ones). Below the list: a **"Reset layout to profile"** action that re-applies
 * the active Profile's layout to the live dockview.
 *
 * Role filtering happens at the caller site via :func:`getVisiblePanels`. The
 * outer row's meta-pill displays the open-count / total ratio.
 */
function PanelsSection({
  expanded,
  onToggleExpanded,
  visiblePanels,
  openPanelIds,
  onTogglePanel,
  onResetLayout,
  busy,
}: {
  expanded: boolean;
  onToggleExpanded: () => void;
  visiblePanels: readonly PanelId[];
  openPanelIds: ReadonlySet<PanelId>;
  onTogglePanel: (id: PanelId) => void;
  onResetLayout: () => void;
  busy: boolean;
}): React.ReactElement {
  const openCount = visiblePanels.filter((id) => openPanelIds.has(id)).length;
  return (
    <div>
      <button
        type="button"
        onClick={onToggleExpanded}
        data-testid="preferences-menu-add-panel-toggle"
        aria-expanded={expanded}
        aria-controls="preferences-menu-add-panel-options"
        className={cn(
          "flex w-full items-center justify-between px-4 py-2.5",
          "text-body-sm text-text-primary hover:bg-bg-elevated",
          "focus:outline-none focus:bg-bg-elevated",
        )}
      >
        <span className="flex items-center gap-2">
          <Plus size={14} />
          Panels
        </span>
        <span className="text-text-muted text-meta" data-testid="preferences-menu-add-panel-count">
          {openCount}/{visiblePanels.length}
        </span>
      </button>
      {expanded && (
        <div
          id="preferences-menu-add-panel-options"
          role="group"
          aria-label="Panels"
          className="border-t border-border bg-bg-base px-1 py-1"
          data-testid="preferences-menu-add-panel-options"
        >
          {visiblePanels.map((id) => {
            const isOpen = openPanelIds.has(id);
            return (
              <button
                key={id}
                type="button"
                disabled={busy}
                onClick={() => onTogglePanel(id)}
                data-testid={`preferences-menu-add-panel-${id}`}
                aria-pressed={isOpen}
                title={
                  isOpen
                    ? `Focus the ${PANEL_TITLES[id]} panel`
                    : `Add the ${PANEL_TITLES[id]} panel`
                }
                className={cn(
                  "flex w-full items-center gap-2 rounded px-3 py-2 text-left",
                  "text-body-sm focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  isOpen
                    ? "text-text-primary hover:bg-bg-elevated focus:bg-bg-elevated"
                    : "text-text-muted hover:bg-bg-elevated focus:bg-bg-elevated hover:text-text-primary",
                )}
              >
                {/* Open / closed indicator dot. Filled green for open
                 * (matches the LIVE topbar indicator semantic);
                 * outlined for closed. Appaloosa pattern. */}
                <span
                  aria-hidden
                  data-testid={`preferences-menu-add-panel-${id}-${isOpen ? "open" : "closed"}-dot`}
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    isOpen ? "bg-positive" : "border border-decor-muted",
                  )}
                />
                <span>{PANEL_TITLES[id]}</span>
                {!isOpen && (
                  <span
                    className="ml-auto text-brand text-meta"
                    data-testid={`preferences-menu-add-panel-${id}-add-label`}
                  >
                    Open
                  </span>
                )}
              </button>
            );
          })}

          {/* Reset layout to profile — re-applies the active Profile's layout to
           * the live dockview (REQ-UI-054). */}
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            disabled={busy}
            onClick={onResetLayout}
            title="Re-apply your active profile's panel layout"
            data-testid="preferences-menu-reset-layout-to-profile"
            className={cn(
              "flex w-full items-center gap-2 rounded px-3 py-2 text-left",
              "text-body-sm text-text-primary",
              "hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <ArrowCounterClockwise size={14} />
            <span>Reset layout to profile</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible Profiles section — ADR-033 §6.1 / Flow U.
 *
 * Lists the Profiles visible to the caller, grouped **Shared** then **User**.
 * Each row exposes ▶ Apply / Export / Update-current-Profile (the pencil) /
 * Delete / Set-as-default; the active Profile is marked (●) and the current
 * login-default carries a filled pin.
 * Below the list: **New Profile…** + **Import Profile…**. Both open the SAME
 * inline name+scope prompt (``prompt``): New names the live config; Import names
 * a Profile created from the imported document (ADR-033 §11.2), name prefilled.
 *
 * Role gating (admin for Shared Edit/Delete; owner for User) is UX-only — the
 * BFF is the primary authorization control (REQ-AUTH-074).
 */
function ProfilesSection({
  expanded,
  onToggleExpanded,
  profiles,
  activeProfileId,
  defaultProfileId,
  isAdmin,
  busy,
  prompt,
  onApply,
  onExport,
  onEdit,
  onDelete,
  onSetDefault,
  onNew,
  onConfirmPrompt,
  onCancelPrompt,
  onImport,
}: {
  expanded: boolean;
  onToggleExpanded: () => void;
  profiles: readonly ProfileSummary[];
  activeProfileId: string | null;
  defaultProfileId: string | null;
  isAdmin: boolean;
  busy: boolean;
  /** The open name+scope prompt's state (``null`` = collapsed); shared by New
   *  and Import. Import mode carries the prefill name + imported document. */
  prompt: ProfilePromptState;
  onApply: (profile: ProfileSummary) => void;
  onExport: (profileId: string) => void;
  onEdit: (profile: ProfileSummary) => void;
  onDelete: (profile: ProfileSummary) => void;
  onSetDefault: (profile: ProfileSummary, isCurrentDefault: boolean) => void;
  onNew: () => void;
  /** Confirm the prompt — the parent routes by ``prompt.mode`` (save-live vs.
   *  create-from-imported-document). */
  onConfirmPrompt: (name: string, scope: "global" | "user") => void;
  onCancelPrompt: () => void;
  onImport: () => void;
}): React.ReactElement {
  const globalProfiles = profiles.filter((p) => p.scope === "global");
  const userProfiles = profiles.filter((p) => p.scope === "user");
  return (
    <div>
      <button
        type="button"
        onClick={onToggleExpanded}
        data-testid="preferences-menu-profiles-toggle"
        aria-expanded={expanded}
        aria-controls="preferences-menu-profiles-options"
        className={cn(
          "flex w-full items-center justify-between px-4 py-2.5",
          "text-body-sm text-text-primary hover:bg-bg-elevated",
          "focus:outline-none focus:bg-bg-elevated",
        )}
      >
        <span className="flex items-center gap-2">
          <UserCircle size={14} />
          Profiles
        </span>
        <span className="text-text-muted text-meta" data-testid="preferences-menu-profiles-count">
          {profiles.length}
        </span>
      </button>
      {expanded && (
        <div
          id="preferences-menu-profiles-options"
          role="group"
          aria-label="Profiles"
          className="border-t border-border bg-bg-base px-1 py-1"
          data-testid="preferences-menu-profiles-options"
        >
          {profiles.length === 0 && (
            <p
              className="px-3 py-2 text-meta text-text-muted"
              data-testid="preferences-menu-profiles-empty"
            >
              No profiles available.
            </p>
          )}

          {globalProfiles.length > 0 && (
            <>
              {/* "Shared" is the display label for the ``scope: "global"``
               * group (item 8, 2026-06-08) — "Global" collided with the map's
               * "global view". The wire value + test-id stay ``global``. */}
              <ProfileGroupLabel label="Shared" testId="profiles-group-global" />
              {globalProfiles.map((profile) => (
                <ProfileRow
                  key={profile.profile_id}
                  profile={profile}
                  isActive={profile.profile_id === activeProfileId}
                  isDefault={profile.profile_id === defaultProfileId}
                  // Shared Profiles: Edit/Delete are admin-only.
                  canManage={isAdmin}
                  busy={busy}
                  onApply={onApply}
                  onExport={onExport}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onSetDefault={onSetDefault}
                />
              ))}
            </>
          )}

          {userProfiles.length > 0 && (
            <>
              <ProfileGroupLabel label="User" testId="profiles-group-user" />
              {userProfiles.map((profile) => (
                <ProfileRow
                  key={profile.profile_id}
                  profile={profile}
                  isActive={profile.profile_id === activeProfileId}
                  isDefault={profile.profile_id === defaultProfileId}
                  // User Profiles: the owner manages them. The list only ever
                  // contains the caller's own User Profiles (REQ-AUTH-065), so
                  // the caller can manage every User row shown.
                  canManage
                  busy={busy}
                  onApply={onApply}
                  onExport={onExport}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onSetDefault={onSetDefault}
                />
              ))}
            </>
          )}

          {/* New / Import actions below the list. Both open the SAME inline
           * name+scope prompt: New names the live config (REQ-UI-076), Import
           * names a Profile created from the parsed imported document
           * (REQ-UI-082 / ADR-033 §11.2; the file picker fires first). While
           * the prompt is open it replaces both buttons. */}
          <div className="my-1 border-t border-border" />
          {prompt !== null ? (
            <NewProfilePrompt
              // Import prefills the export's name; New starts blank.
              initialName={prompt.mode === "import" ? prompt.name : ""}
              // Import defaults to User (the always-allowed scope; admins may
              // still switch to Shared in the prompt). New keeps the prior
              // default (admins default to Shared). Shared stays admin-gated.
              defaultScope={prompt.mode === "import" ? "user" : isAdmin ? "global" : "user"}
              canSelectGlobal={isAdmin}
              busy={busy}
              onCancel={onCancelPrompt}
              onSave={onConfirmPrompt}
            />
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={onNew}
                data-testid="preferences-menu-profiles-new"
                className={cn(
                  "flex w-full items-center gap-2 rounded px-3 py-2 text-left",
                  "text-body-sm text-text-primary",
                  "hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <Plus size={14} />
                <span>New Profile…</span>
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={onImport}
                title="Import a profile from a JSON export file"
                data-testid="preferences-menu-profiles-import"
                className={cn(
                  "flex w-full items-center gap-2 rounded px-3 py-2 text-left",
                  "text-body-sm text-text-primary",
                  "hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <UploadSimple size={14} />
                <span>Import Profile…</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The shared inline name+scope prompt — used by **New Profile…** (name the live
 * config) and **Import Profile…** (name a Profile created from a parsed import,
 * ADR-033 §11.2). A name input (required, unique within the chosen scope —
 * uniqueness enforced server-side via 409) + a scope radio (User / Shared). The
 * Shared radio is admin-gated with a "requires administrator" hint for
 * non-admins (REQ-UI-078). Save is disabled until a name is entered. Mirrors the
 * Settings panel's Save-as-new form so the surfaces read identically.
 *
 * ``initialName`` prefills the name (Import passes the export's name; New passes
 * ``""``). ``defaultScope`` seeds the radio (New → admin's default; Import →
 * User, the always-allowed scope). Both seeds are honoured on (re)mount — the
 * prompt is freshly mounted each time it opens, so a new ``initialName`` /
 * ``defaultScope`` takes effect.
 *
 * **Display label vs. wire value:** the scope the user sees as **"Shared"** is
 * the ``scope: "global"`` wire value (renamed in the UI only). The
 * ``scope === "global"`` literal + the ``-scope-global`` test-id are unchanged.
 */
function NewProfilePrompt({
  initialName = "",
  defaultScope,
  canSelectGlobal,
  busy,
  onCancel,
  onSave,
}: {
  /** Prefill for the name input — the export's name for Import; ``""`` for New. */
  initialName?: string;
  /** Initial scope selection — clamped to ``"user"`` when the caller can't
   *  select Shared (a non-admin), so a stale ``"global"`` seed never sticks. */
  defaultScope: "global" | "user";
  canSelectGlobal: boolean;
  busy: boolean;
  onCancel: () => void;
  onSave: (name: string, scope: "global" | "user") => void;
}): React.ReactElement {
  const [name, setName] = useState(initialName);
  const [scope, setScope] = useState<"global" | "user">(
    // A non-admin can never select Shared — clamp the seed to User so the
    // disabled radio + the persisted scope agree.
    defaultScope === "global" && !canSelectGlobal ? "user" : defaultScope,
  );
  const trimmed = name.trim();
  return (
    <div
      data-testid="preferences-menu-profiles-new-form"
      className="mt-1 flex flex-col gap-2 rounded border border-border bg-bg-surface p-3"
    >
      <label className="flex flex-col gap-1">
        <span className="text-body-sm text-text-secondary">New profile name</span>
        <input
          type="text"
          value={name}
          required
          autoFocus
          onChange={(e) => setName(e.target.value)}
          data-testid="preferences-menu-profiles-new-name"
          className={cn(
            "rounded border border-border bg-bg-base px-2 py-1.5 text-body-sm text-text-primary",
            "focus:border-brand focus:outline-none",
          )}
        />
      </label>
      <fieldset disabled={busy}>
        <legend className="text-body-sm text-text-secondary">Scope</legend>
        <div className="mt-1 flex flex-col gap-1">
          <label className="flex items-center gap-2 text-body-sm text-text-primary">
            <input
              type="radio"
              name="new-profile-scope"
              checked={scope === "user"}
              onChange={() => setScope("user")}
              data-testid="preferences-menu-profiles-new-scope-user"
            />
            User
          </label>
          <label
            className={cn(
              "flex items-center gap-2 text-body-sm",
              canSelectGlobal ? "text-text-primary" : "text-text-disabled",
            )}
            title={canSelectGlobal ? undefined : "Requires administrator"}
          >
            <input
              type="radio"
              name="new-profile-scope"
              checked={scope === "global"}
              disabled={!canSelectGlobal}
              onChange={() => setScope("global")}
              data-testid="preferences-menu-profiles-new-scope-global"
            />
            Shared
            {!canSelectGlobal && (
              <span
                className="text-meta text-text-muted"
                data-testid="preferences-menu-profiles-new-scope-global-hint"
              >
                (requires administrator)
              </span>
            )}
          </label>
        </div>
      </fieldset>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="preferences-menu-profiles-new-cancel"
          className="rounded border border-border px-3 py-1.5 text-body-sm text-text-secondary hover:bg-bg-elevated"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || trimmed.length === 0}
          onClick={() => onSave(trimmed, scope)}
          data-testid="preferences-menu-profiles-new-confirm"
          className={cn(
            "rounded bg-brand px-3 py-1.5 text-body-sm text-bg-base hover:bg-brand-dim",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          Save profile
        </button>
      </div>
    </div>
  );
}

/**
 * Group divider label (Shared / User) inside the Profiles section. A
 * non-uppercase 12 px ``text-body-sm`` ``font-medium`` label, sized to the
 * dockview tab labels + the gear-menu section rows (architect 2026-06-08,
 * formatting pass item 1) — NOT the (undefined) ``text-caption`` token that
 * fell back to the 16 px browser default.
 */
function ProfileGroupLabel({
  label,
  testId,
}: {
  label: string;
  testId: string;
}): React.ReactElement {
  return (
    <p
      className="px-3 pb-1 pt-2 text-body-sm font-medium text-text-secondary"
      data-testid={`preferences-menu-${testId}`}
    >
      {label}
    </p>
  );
}

/**
 * A single Profile row: the name + action affordances. Apply, Export, Update
 * current Profile (the pencil), Delete, and Set-as-default are all wired. The
 * pencil + Delete render only when ``canManage`` (admin for Shared, owner for
 * User — REQ-UI-079/080); a built-in Profile never offers Delete (REQ-UI-080)
 * but still offers the pencil update.
 *
 * The **active** Profile (``isActive``) is marked with a leading ● and shows
 * "Active" in place of the Apply button. The **default** Profile (``isDefault``)
 * carries a filled pin (the login-default); the pin toggles the login-default
 * pointer via ``onSetDefault`` (ADR-033 §3.2).
 */
function ProfileRow({
  profile,
  isActive,
  isDefault,
  canManage,
  busy,
  onApply,
  onExport,
  onEdit,
  onDelete,
  onSetDefault,
}: {
  profile: ProfileSummary;
  isActive: boolean;
  isDefault: boolean;
  canManage: boolean;
  busy: boolean;
  onApply: (profile: ProfileSummary) => void;
  onExport: (profileId: string) => void;
  onEdit: (profile: ProfileSummary) => void;
  onDelete: (profile: ProfileSummary) => void;
  onSetDefault: (profile: ProfileSummary, isCurrentDefault: boolean) => void;
}): React.ReactElement {
  return (
    <div
      className="flex items-center gap-1 rounded px-3 py-1.5 hover:bg-bg-elevated"
      data-testid={`preferences-menu-profile-${profile.profile_id}`}
      data-active={isActive ? "true" : undefined}
      data-default={isDefault ? "true" : undefined}
    >
      <span className="flex-1 truncate text-body-sm text-text-primary" title={profile.name}>
        {isActive && (
          <span
            aria-hidden
            className="mr-1.5 text-brand"
            data-testid={`preferences-menu-profile-${profile.profile_id}-active-dot`}
          >
            ●
          </span>
        )}
        {profile.name}
        {profile.is_builtin && (
          <span
            className="ml-1.5 text-meta text-text-muted"
            data-testid={`preferences-menu-profile-${profile.profile_id}-builtin`}
          >
            built-in
          </span>
        )}
        {isDefault && (
          <span
            className="ml-1.5 text-meta text-text-secondary"
            data-testid={`preferences-menu-profile-${profile.profile_id}-default-label`}
          >
            default
          </span>
        )}
      </span>

      {/* Set as default — a pin toggle (ADR-033 §3.2; REQ-UI-094). Filled pin
       * when this is the current login-default (click clears it); outlined pin
       * otherwise (click sets it). Keyboard-operable + labelled. */}
      <button
        type="button"
        disabled={busy}
        onClick={() => onSetDefault(profile, isDefault)}
        title={isDefault ? "Clear default profile" : "Set as default profile"}
        aria-label={
          isDefault ? `Clear ${profile.name} as default` : `Set ${profile.name} as default`
        }
        aria-pressed={isDefault}
        data-testid={`preferences-menu-profile-${profile.profile_id}-set-default`}
        className={cn(
          "rounded p-1",
          isDefault ? "text-brand" : "text-text-secondary hover:text-text-primary",
          "hover:bg-bg-overlay focus:outline-none focus:bg-bg-overlay",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {isDefault ? (
          <PushPin size={14} weight="fill" aria-hidden />
        ) : (
          <PushPinSlash size={14} aria-hidden />
        )}
      </button>

      {/* Apply — wired (REQ-UI-073 / 075). A play (▶) icon button: keyboard-
       * operable + labelled (REQ-UI-094). The active Profile shows a static
       * "Active" marker instead (re-applying is a no-op). */}
      {isActive ? (
        <span
          className="rounded px-2 py-1 text-meta text-brand"
          data-testid={`preferences-menu-profile-${profile.profile_id}-active`}
        >
          Active
        </span>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => onApply(profile)}
          title="Apply this profile"
          aria-label={`Apply ${profile.name}`}
          data-testid={`preferences-menu-profile-${profile.profile_id}-apply`}
          className={cn(
            "rounded p-1 text-brand",
            "hover:bg-bg-overlay focus:outline-none focus:bg-bg-overlay",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <Play size={14} weight="fill" aria-hidden />
        </button>
      )}

      {/* Export — wired. */}
      <button
        type="button"
        disabled={busy}
        onClick={() => onExport(profile.profile_id)}
        title={`Export ${profile.name}`}
        aria-label={`Export ${profile.name}`}
        data-testid={`preferences-menu-profile-${profile.profile_id}-export`}
        className={cn(
          "rounded p-1 text-text-secondary",
          "hover:bg-bg-overlay hover:text-text-primary focus:outline-none focus:bg-bg-overlay",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <Export size={14} />
      </button>

      {/* Update current Profile — wired (diff-aware over the FULL live document:
       * working_state + live layout + live map camera); admin (Shared) / owner
       * (User) only. Confirms (unless already identical — "Nothing to update"),
       * then updates this Profile to the live configuration. Relabelled from
       * "Edit" 2026-06-08 (the pencil performs an update-from-live, not an
       * open-editor) to match the Settings-panel footer button. The visible
       * affordance is the pencil glyph; ``aria-label`` / ``title`` carry the
       * action name (REQ-UI-094). */}
      {canManage && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onEdit(profile)}
          title={`Update ${profile.name} to current settings`}
          aria-label={`Update current Profile (${profile.name})`}
          data-testid={`preferences-menu-profile-${profile.profile_id}-edit`}
          className={cn(
            "rounded p-1 text-text-secondary",
            "hover:bg-bg-overlay hover:text-text-primary focus:outline-none focus:bg-bg-overlay",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <PencilSimple size={14} />
        </button>
      )}

      {/* Delete — wired; manageable + non-builtin only. Confirms then deletes
       * (REQ-UI-080). */}
      {canManage && !profile.is_builtin && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onDelete(profile)}
          title={`Delete ${profile.name}`}
          aria-label={`Delete ${profile.name}`}
          data-testid={`preferences-menu-profile-${profile.profile_id}-delete`}
          className={cn(
            "rounded p-1 text-text-secondary",
            "hover:bg-bg-overlay hover:text-destructive focus:outline-none focus:bg-bg-overlay",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <Trash size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * Coerce a permissive ``ThemeMode | unknown`` value into the
 * strict :type:`Theme` union the menu works in. Falls back to
 * ``"dark"`` per the 2026-05-26 amendment's default-preferences
 * contract.
 */
function coerceTheme(value: ThemeMode | undefined): Theme {
  if (value === "light" || value === "dark" || value === "system") {
    return value;
  }
  return "dark";
}
